import { ApolloQueryResult } from '@apollo/client';

import { Option } from '@components/fields/AsyncSelect';
import { ImageName } from '@utils/imageUtils';

export function mapSearchToOptions<Model extends unknown>(
  models: (Model | null)[],
  getName: (model: Model | null) => string,
  getValue: (model: Model | null) => string,
  defaultOptions?: Option[],
  getImageName?: (model: Model | null) => ImageName | undefined
): Option[] {
  const options: Option[] = models.map(model => ({
    imageName: getImageName ? getImageName(model) : undefined,
    label: getName(model),
    value: getValue(model)
  }));

  const sortedOptions = options.sort((a, b) => (a.label.toLowerCase() > b.label.toLowerCase() ? 1 : -1));

  if (defaultOptions) {
    return defaultOptions.concat(sortedOptions);
  }

  return sortedOptions;
}

type Item = {
  id: string;
};

type Props<T extends unknown> = {
  defaultOptions: Option[];
  excludeValue?: string;
  getLabel: (t: T | null) => string;
  getValue: (t: T | null) => string;
  loadOptionsOnOpen?: boolean;
  query: Promise<ApolloQueryResult<unknown>>;
  searchTerm: string;
};

type Edge = {
  node: Item | null;
};

type Connection = {
  edges: (Edge | null)[] | null;
};

type Result = {
  items: Connection | undefined;
};

export const searchOptions = async <T extends unknown>({
  defaultOptions,
  excludeValue,
  getLabel,
  getValue,
  loadOptionsOnOpen = false,
  query,
  searchTerm
}: Props<T>) =>
  new Promise<Option[]>(resolve => {
    if (loadOptionsOnOpen || searchTerm.length > 0) {
      query.then(data => {
        const items = Object.keys(data.data as Result)[0];
        // @ts-expect-error get the items
        const ourData = data?.data[items] as unknown as Connection;
        const edges = ourData?.edges;
        const result: Item[] = [];

        if (edges) {
          edges.forEach(edge => {
            if (edge?.node && edge?.node?.id !== excludeValue) {
              result.push(edge.node);
            }
          });
        }
        if (ourData) {
          resolve(
            mapSearchToOptions<Item>(
              result,
              model => getLabel(model as unknown as T) ?? '',
              model => getValue(model as unknown as T) ?? '',
              defaultOptions
            )
          );
        } else {
          resolve([]);
        }
      });
    }
  });
